import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Camera, Check, Loader2, Heart, ArrowLeft } from 'lucide-react';
import api from '../utils/api';
import { isAuthenticated } from '../utils/auth';

export default function VerifyPhoto() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Check if user is eligible for free matches and needs verification
    const checkEligibility = async () => {
      try {
        const res = await api.get('/user/profile');
        
        // If already verified, redirect to dashboard
        if (res.data.is_photo_verified) {
          navigate('/dashboard');
          return;
        }
        
        // If not eligible for free matches, redirect to paywall
        if (!res.data.gets_free_matches) {
          navigate('/paywall');
          return;
        }
        
        setLoading(false);
        startCamera();
      } catch (error) {
        console.error('Error checking eligibility:', error);
        navigate('/dashboard');
      }
    };

    checkEligibility();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [navigate]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          setCameraReady(true);
        };
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setPermissionDenied(true);
      toast.error('Camera access required for verification');
    }
  };

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    setCapturing(true);
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(imageDataUrl);
    setCapturing(false);
  };

  const handleRetake = () => {
    setCapturedImage(null);
  };

  const handleSubmit = async () => {
    if (!capturedImage) return;
    
    setUploading(true);
    try {
      // Convert data URL to Blob
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
      
      const formData = new FormData();
      formData.append('file', file);

      await api.post('/user/upload-verification-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Stop camera
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      toast.success('Photo verified! Welcome to BlindMatch!');
      navigate('/dashboard');
    } catch (error) {
      // Show detailed error message from backend
      const errorMsg = error.response?.data?.detail || 'Verification failed. Please ensure you take a clear selfie of yourself.';
      toast.error(errorMsg);
      setCapturedImage(null); // Allow retake
    } finally {
      setUploading(false);
    }
  };

  const handleSkipVerification = () => {
    // If user skips, they go to paywall to pay instead
    navigate('/paywall');
  };

  if (loading) {
    return (
      <div className="app-container gradient-bg min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={40} />
      </div>
    );
  }

  return (
    <div className="app-container gradient-bg">
      <div className="px-4 py-6 pb-20 min-h-screen">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => navigate('/profile-setup')}
            className="w-10 h-10 rounded-full bg-secondary/50 flex items-center justify-center hover:bg-secondary/70 transition-colors"
          >
            <ArrowLeft className="text-white" size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Verify Your Profile</h1>
            <p className="text-gray-400 text-sm">Quick selfie to confirm you're real</p>
          </div>
        </div>

        {/* Info Card */}
        <div className="glass rounded-2xl p-4 mb-6 border border-green-500/30">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
              <Heart className="text-green-400" size={20} />
            </div>
            <div>
              <h3 className="text-white font-semibold mb-1">Get Started Free!</h3>
              <p className="text-gray-300 text-sm">
                Verify with a quick selfie and get your matches instantly. No payment required!
              </p>
            </div>
          </div>
        </div>

        {permissionDenied ? (
          <div className="text-center py-8">
            <Camera className="text-red-500 mx-auto mb-4" size={48} />
            <p className="text-red-400 mb-4">Camera access denied</p>
            <p className="text-gray-400 text-sm mb-4">
              Please allow camera access in your browser settings to verify your profile.
            </p>
            <button
              onClick={handleSkipVerification}
              className="w-full h-12 bg-secondary/50 text-white font-semibold rounded-full border border-white/10"
            >
              Continue with Payment Instead
            </button>
          </div>
        ) : (
          <>
            {/* Camera View / Captured Image */}
            <div className="relative rounded-2xl overflow-hidden mb-4 bg-black aspect-video">
              {capturedImage ? (
                <img 
                  src={capturedImage} 
                  alt="Captured selfie" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <>
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    muted
                    className="w-full h-full object-cover mirror"
                  />
                  {!cameraReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <Loader2 className="animate-spin text-primary" size={32} />
                    </div>
                  )}
                  {/* Face guide overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-48 h-60 border-2 border-dashed border-primary/50 rounded-full" />
                  </div>
                </>
              )}
            </div>
            
            {/* Hidden canvas for capturing */}
            <canvas ref={canvasRef} className="hidden" />

            {/* Instructions */}
            <div className="glass rounded-xl p-3 mb-4 border border-primary/20">
              <p className="text-gray-300 text-xs text-center">
                Position your face within the oval guide and ensure good lighting
              </p>
            </div>

            {/* Action Buttons */}
            {capturedImage ? (
              <div className="space-y-3">
                <button
                  onClick={handleSubmit}
                  disabled={uploading}
                  className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-full transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="verify-submit-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Check size={20} />
                      Verify & Get Matches
                    </>
                  )}
                </button>
                <button
                  onClick={handleRetake}
                  disabled={uploading}
                  className="w-full h-10 bg-secondary/50 text-white font-medium rounded-full border border-white/10 disabled:opacity-50"
                >
                  Retake Photo
                </button>
              </div>
            ) : (
              <button
                onClick={handleCapture}
                disabled={!cameraReady || capturing}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="capture-selfie-btn"
              >
                {capturing ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    <Camera size={20} />
                    Capture Selfie
                  </>
                )}
              </button>
            )}

            {/* Skip Option */}
            <button
              onClick={handleSkipVerification}
              className="w-full h-10 text-gray-400 hover:text-white transition-colors mt-4 text-sm"
            >
              Skip verification & pay instead
            </button>
          </>
        )}
      </div>

      <style>{`
        .mirror {
          transform: scaleX(-1);
        }
      `}</style>
    </div>
  );
}
